/**
 * Player clock (03 §2, §10).
 *
 * - Director mode: video time v advances by the wall dt; raceTime = warp.raceT(v), so the title and end
 *   holds stand still in race time while v moves (exactly the v6 videos).
 * - Free mode: raceTime += dt · rate (1 / 5 / 20 / 60).
 * - Presentation time drives every HUD animation. Ages are pure functions of (raceTime, mode) apart from
 *   the holds, where the Director video time is the state (as in the v6 renderers: age = (k − k_event)/30):
 *     director: age = v − ageFrame(tEvent)/fps, ageFrame = frameForRaceT(t), first 'lap1' frame for t ≤ 0
 *     free:     age = (raceTime − tEvent) / rate
 * - Switching mode keeps the race time. A Director seek to t puts v on the v6 frame that first shows t,
 *   k = frameForRaceT(t) (so every event with t_e ≤ t has age ≥ 0, exactly as in the video), and keeps
 *   raceTime = t; the next tick continues from raceT(v + dt) ≥ race_t[k] ≥ t (never backwards). For t on a
 *   frame time this is identical to playing to frame k.
 */
import type { ClockApi, Mode } from '../api.ts';
import type { TimeWarp } from './timewarp.ts';

export const PRESENTATION_FPS = 30;
export type ClockEvent = 'seek' | 'mode' | 'play';

/** an event's time, or an explicit Director video frame to age from (events.json `age_frame`) */
export type AgeRef = number | { frame: number };

export class Clock implements ClockApi {
  private _raceTime = 0;
  private _v = 0;
  private _playing = false;
  private _mode: Mode = { kind: 'director' };
  private readonly listeners: Record<ClockEvent, Set<() => void>> = { seek: new Set(), mode: new Set(), play: new Set() };
  /** last race time of the timeline ((F − 1)/hz); free mode clamps to it */
  readonly tEnd: number;

  constructor(readonly warp: TimeWarp, opts?: { tEnd?: number; mode?: Mode; raceTime?: number }) {
    this.tEnd = opts?.tEnd ?? warp.tHold;
    if (opts?.mode) this._mode = opts.mode;
    this.seekSilently(opts?.raceTime ?? 0);
  }

  get raceTime(): number { return this._raceTime; }
  get playing(): boolean { return this._playing; }
  get mode(): Mode { return this._mode; }

  /** Director: v (incl. holds). Free: the Director video time of the current race time. */
  get videoTime(): number {
    return this._mode.kind === 'director' ? this._v : this.warp.videoT(this._raceTime);
  }

  /** the v6 frame index of videoTime */
  get videoFrame(): number {
    return this.warp.frameAt(this.videoTime);
  }

  /** race seconds per presentation second, independent of pause (Director: the warp speed, 0 in holds) */
  get effectiveRate(): number {
    return this._mode.kind === 'director' ? this.warp.speed(this._v) : this._mode.rate;
  }

  /** Director parity rule: the race time of the current v6 frame (discrete values are held here) */
  get frameRaceTime(): number {
    if (this._mode.kind !== 'director') return this._raceTime;
    return this.warp.raceTable[this.warp.frameAt(this._v)] ?? this._raceTime;
  }

  /** v6 speed label ('5X', '12.4X', the pause word in the holds — default v6 'PAUSE'); free mode: the rate ('20X') */
  speedLabel(): string {
    return this._mode.kind === 'director' ? this.warp.speedLabel(this._v) : `${String(this._mode.rate)}X`;
  }

  /** presentation time (s): Director video time, or race time / rate in free mode */
  get presentationTime(): number {
    return this._mode.kind === 'director' ? this._v : this._raceTime / this._mode.rate;
  }

  tick(dtWallSec: number): void {
    if (!this._playing || !(dtWallSec > 0)) return;
    if (this._mode.kind === 'director') {
      const vEnd = (this.warp.n - 1) / this.warp.fps;
      this._v = Math.min(this._v + dtWallSec, vEnd);
      this._raceTime = this.warp.raceT(this._v);
      if (this._v >= vEnd) this.setPlaying(false);
    } else {
      this._raceTime = Math.min(this._raceTime + dtWallSec * this._mode.rate, this.tEnd);
      if (this._raceTime >= this.tEnd) this.setPlaying(false);
    }
  }

  /** Director: round the video position to a whole frame (LITE 30 fps shows only whole video frames). */
  snapToFrame(): void {
    if (this._mode.kind !== 'director') return;
    this._v = Math.round(this._v * this.warp.fps) / this.warp.fps;
    this._raceTime = this.warp.raceT(this._v);
  }

  private seekSilently(t: number): void {
    const tt = Number.isFinite(t) ? t : 0;
    if (this._mode.kind === 'director') {
      this._raceTime = Math.min(Math.max(tt, 0), this.warp.tHold);
      this._v = this.warp.frameForRaceT(this._raceTime) / this.warp.fps;
    } else {
      this._raceTime = Math.min(Math.max(tt, 0), this.tEnd);
    }
  }

  seek(raceTime: number): void {
    this.seekSilently(raceTime);
    this.emit('seek');
  }

  /** Director only: seek by video time (scrubbing the v6 timeline, including the holds) */
  seekVideo(v: number): void {
    if (this._mode.kind !== 'director') {
      this.seek(this.warp.raceT(v));
      return;
    }
    this._v = this.warp.frameF(v) / this.warp.fps;
    this._raceTime = this.warp.raceT(this._v);
    this.emit('seek');
  }

  setPlaying(p: boolean): void {
    if (p === this._playing) return;
    this._playing = p;
    this.emit('play');
  }

  setMode(m: Mode): void {
    const same = m.kind === this._mode.kind && (m.kind === 'director' || (this._mode.kind === 'free' && this._mode.rate === m.rate));
    if (same) return;
    const t = this._raceTime;
    this._mode = m.kind === 'director' ? { kind: 'director' } : { kind: 'free', rate: m.rate };
    this.seekSilently(t);                                // keeps t unless the new mode's range clamps it
    this.emit('mode');
    if (this._raceTime !== t) this.emit('seek');
  }

  /** seconds of presentation time since the event */
  presentationAge(tEvent: AgeRef): number {
    if (this._mode.kind === 'director') {
      const k = typeof tEvent === 'number' ? this.warp.ageFrame(tEvent) : tEvent.frame;
      return this._v - k / this.warp.fps;
    }
    const te = typeof tEvent === 'number' ? tEvent : (this.warp.raceTable[tEvent.frame] ?? 0);
    return (this._raceTime - te) / this._mode.rate;
  }

  /** presentation frames (30 fps) since the event; Director: integer v6 frames (k − k_event) */
  presentationAgeFrames(tEvent: AgeRef): number {
    if (this._mode.kind === 'director') {
      const k = typeof tEvent === 'number' ? this.warp.ageFrame(tEvent) : tEvent.frame;
      return this.videoFrame - k;
    }
    return this.presentationAge(tEvent) * PRESENTATION_FPS;
  }

  /** race time n presentation frames ago (clamped at 0) */
  raceTimeAgo(nFrames: number): number {
    if (this._mode.kind === 'director') return this.warp.raceT(Math.max(this._v - nFrames / this.warp.fps, 0));
    return Math.max(this._raceTime - (nFrames / PRESENTATION_FPS) * this._mode.rate, 0);
  }

  racePerPresentationFrame(): number {
    return this.effectiveRate / PRESENTATION_FPS;
  }

  /** deterministic 0..1 blink phase at hz cycles per presentation second */
  pulse(hz: number): number {
    const x = this.presentationTime * hz;
    return x - Math.floor(x);
  }

  on(ev: ClockEvent, fn: () => void): () => void {
    this.listeners[ev].add(fn);
    return () => { this.listeners[ev].delete(fn); };
  }

  private emit(ev: ClockEvent): void {
    for (const fn of [...this.listeners[ev]]) fn();
  }
}
